import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@material-ui/styles";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import BackButton from "../../components/redesign/elements/BackButton";
import CampaignDetailDashboard from "./CampaignDetailDashboard";
import Loader from "../../components/elements/Loader";
import api from "../../api";
import redesignClasses from "./CampaignDetailRedesign.module.scss";
import { BrandgenCampaignPool, BrandgenCampaignPoolReport, ReportTimeFrames } from "../../types/campaignTypes";
import { CampaignDashboardStylesProvider } from "../AdvertiserDetailPage/CampaignsDashboard/CampaignDashboardStylesContext";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { useSetPageMeta } from "../../layouts/AppShellLayout/PageMetaContext";

const PageRoot = styled("div")({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
});

const fetchCampaignById = async (campaignId: string): Promise<BrandgenCampaignPool> => {
  const { payload } = await api.getCampaignById(campaignId);
  return payload;
};

const fetchCampaignDailyReport = async (campaignId: string, date: Date) => {
  const { payload } = await api.getCampaignDailyReport(campaignId, date);
  return payload;
};

const CampaignDetailPage: FC = () => {
  const { advertiserId, campaignId } = useParams<{ advertiserId: string; campaignId: string }>();
  const location = useLocation();
  const isAdminView = location.pathname.startsWith("/administration");
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency?.advertisers) ?? [];

  const advertiser = useMemo(
    () => advertisers.find((item) => item.brandgenAdvertiserId === advertiserId),
    [advertisers, advertiserId],
  );
  const storedAdvertiserName = advertiser?.name;

  const [campaign, setCampaign] = useState<BrandgenCampaignPool | null>(null);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<ReportTimeFrames>(ReportTimeFrames.ALL_TIME);
  const [selectedCampaignPoolReport, setSelectedCampaignPoolReport] = useState<BrandgenCampaignPoolReport | null>(null);
  const [customReportDate, setCustomReportDate] = useState<Date>();
  const [isCustomDateChanged, setIsCustomDateChanged] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchedAdvertiserName, setFetchedAdvertiserName] = useState<string | null>(null);

  const advertiserName = storedAdvertiserName ?? fetchedAdvertiserName ?? "Advertiser";
  const campaignName = campaign?.name ?? "Campaign";

  useSetPageMeta(campaignName, `Campaign details for ${advertiserName}`);

  useEffect(() => {
    let cancelled = false;

    if (storedAdvertiserName) {
      setFetchedAdvertiserName(storedAdvertiserName);
    } else {
      const loadAdvertiser = async () => {
        try {
          const { payload } = await api.getAdvertiserById(advertiserId);
          if (!cancelled && payload?.name) {
            setFetchedAdvertiserName(payload.name);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log("fetchAdvertiserById error:", error);
        }
      };

      loadAdvertiser();
    }

    return () => {
      cancelled = true;
    };
  }, [storedAdvertiserName, advertiserId]);

  const fetchAndSetCampaign = useCallback(async () => {
    try {
      const fetchedCampaign = await fetchCampaignById(campaignId);
      setCampaign(fetchedCampaign);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("fetchAndSetCampaign error:", error);
      setCampaign(null);
    }
  }, [campaignId]);

  useEffect(() => {
    const loadCampaign = async () => {
      try {
        setIsLoading(true);
        await fetchAndSetCampaign();
      } finally {
        setIsLoading(false);
      }
    };

    loadCampaign();
  }, [fetchAndSetCampaign]);

  const handleTimeFrameChange = useCallback(
    (
      event: React.ChangeEvent<{
        value: unknown;
        custom_date?: Date;
      }>,
    ) => {
      const { value } = event.target;
      const date = event.target?.custom_date;

      if (
        value === ReportTimeFrames.CUSTOM_DATE &&
        date &&
        new Date(date).getTime() !== new Date(customReportDate ?? "").getTime()
      ) {
        setCustomReportDate(date);
        setIsCustomDateChanged(true);
      } else {
        setIsCustomDateChanged(false);
      }

      setSelectedTimeFrame(value as ReportTimeFrames);
    },
    [customReportDate],
  );

  useEffect(() => {
    const fetchCustomDateCampaignReport = async () => {
      setIsFetching(true);

      if (selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE && campaign && customReportDate && isCustomDateChanged) {
        try {
          const report = await fetchCampaignDailyReport(campaign.brandgenCampaignPoolId, customReportDate);
          setSelectedCampaignPoolReport(report);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log("fetchCustomDateCampaignReport error:", error);
        }
      }

      setIsFetching(false);
    };

    fetchCustomDateCampaignReport();
  }, [campaign, customReportDate, selectedTimeFrame, isCustomDateChanged]);

  const campaignsBackPath = isAdminView ? `/administration/${advertiserId}` : `/advertisers/${advertiserId}/dashboard`;

  if (isLoading) {
    return <Loader />;
  }

  if (!campaign) {
    return (
      <PageRoot>
        <BackButton label="Back to Campaigns" to={campaignsBackPath} />
        <p>Campaign not found.</p>
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <BackButton label="Back to Campaigns" to={campaignsBackPath} />
      <CampaignDashboardStylesProvider value={redesignClasses}>
        <CampaignDetailDashboard
          selectedCampaignPool={campaign}
          selectedTimeFrame={selectedTimeFrame}
          handleTimeFrameChange={handleTimeFrameChange}
          fetchAndSetCampaign={fetchAndSetCampaign}
          selectedCampaignPoolReport={selectedCampaignPoolReport}
          customReportDate={customReportDate}
          isFetching={isFetching}
          isCustomDateChanged={isCustomDateChanged}
        />
      </CampaignDashboardStylesProvider>
    </PageRoot>
  );
};

export default CampaignDetailPage;
